
import { motion } from "framer-motion";
import { useAgentStore, type NodeState } from "@/store/agentStore";
import "./AgentGraph.css";

const NODES = [
  { id: "supervisor",      label: "Supervisor",      icon: "⚡", desc: "Plans queries & goals" },
  { id: "sql_fetcher",     label: "SQL Fetcher",      icon: "🗄",  desc: "Executes database queries" },
  { id: "data_analyzer",   label: "Data Analyzer",    icon: "🔬", desc: "Pandas stats + LLM interpretation" },
  { id: "chart_generator", label: "Chart Generator",  icon: "📊", desc: "Plotly visualizations" },
  { id: "insight_engine",  label: "Insight Engine",   icon: "💡", desc: "AI business insights" },
];

const COLOR: Record<string, string> = {
  supervisor:      "#f59e0b",
  sql_fetcher:     "#3b82f6",
  data_analyzer:   "#8b5cf6",
  chart_generator: "#10b981",
  insight_engine:  "#f43f5e",
};

function NodeCard({ node, state }: { node: typeof NODES[0]; state: NodeState }) {
  const color = COLOR[node.id];
  const isActive = state.status === "active";
  const isDone   = state.status === "done";
  const isError  = state.status === "error";

  const cardClass = `node-card${isActive ? " node-card--active" : isDone ? " node-card--done" : ""}`;
  const iconClass = `node-icon${isActive ? " node-icon--active" : isDone ? " node-icon--done" : ""}`;
  const labelClass = `node-label${isActive ? " node-label--active" : isDone ? " node-label--done" : ""}`;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: -12 }}
      animate={{ opacity: 1, x: 0 }}
      className={cardClass}
      style={{
        "--node-color": color,
        "--node-color-bg": `${color}12`,
        "--node-color-shadow": `${color}30`,
        "--node-icon-bg": `${color}20`,
        "--node-color-faded": `${color}cc`,
      } as React.CSSProperties}
    >
      {/* Icon */}
      <div className={iconClass}>{node.icon}</div>

      {/* Labels */}
      <div className="node-labels">
        <div className={labelClass}>{node.label}</div>
        <div className="node-desc">
          {isActive ? (
            <span className="node-desc-running">running…</span>
          ) : isDone && state.elapsed_ms ? (
            <span className="node-desc-done">✓ {(state.elapsed_ms / 1000).toFixed(1)}s</span>
          ) : (
            node.desc
          )}
        </div>
      </div>

      {/* Status dot */}
      <div className="node-status">
        {isActive && (
          <motion.div
            animate={{ scale: [1, 1.4, 1], opacity: [1, 0.5, 1] }}
            transition={{ duration: 1.2, repeat: Infinity }}
            className="node-active-dot"
          />
        )}
        {isDone && <span className="node-done-check">✓</span>}
        {isError && <span className="node-error-mark">✗</span>}
        {state.status === "pending" && <div className="node-pending-dot" />}
      </div>
    </motion.div>
  );
}

export function AgentGraph() {
  const current = useAgentStore((s) => s.current);
  const nodes = current?.nodes ?? {};

  return (
    <div className="agent-graph">
      {NODES.map((node, i) => (
        <div key={node.id}>
          <NodeCard node={node} state={nodes[node.id] ?? { status: "pending" }} />
          {i < NODES.length - 1 && (
            <div className="node-connector">
              <div className={`node-connector-line${nodes[node.id]?.status === "done" ? " node-connector-line--done" : ""}`} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
